import React, { useState } from 'react';
import { getErrorMessage } from '../utils/errorHandler';

interface MonthlyProcessSectionProps {
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

interface MonthlyProcessResult {
  target_year: number;
  target_month: number;
  processed_count: number;
  success_count: number;
  error_count: number;
  total_depreciation_amount: number;
  journal_entries_created: number;
  errors: string[];
}

const MonthlyProcessSection: React.FC<MonthlyProcessSectionProps> = ({ apiFetch }) => {
  const currentDate = new Date();
  const [targetYear, setTargetYear] = useState<number>(currentDate.getFullYear());
  const [targetMonth, setTargetMonth] = useState<number>(currentDate.getMonth() + 1);
  const [processing, setProcessing] = useState<boolean>(false);
  const [result, setResult] = useState<MonthlyProcessResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExecute = async () => {
    if (!window.confirm(`${targetYear}年${targetMonth}月の月次処理を実行しますか？\n\nこの処理により、該当月の減価償却費が計算され、仕訳が自動生成されます。`)) {
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiFetch('/api/fixed-assets/monthly-process', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_year: targetYear,
          target_month: targetMonth,
        }),
      });

      if (!response.ok) {
        const message = await getErrorMessage(response, '月次処理の実行に失敗しました。');
        throw new Error(message);
      }

      const data: MonthlyProcessResult = await response.json();
      setResult(data);
    } catch (err: any) {
      setError(err?.message || '月次処理の実行に失敗しました。');
    } finally {
      setProcessing(false);
    }
  };

  const years = Array.from({ length: 20 }, (_, i) => currentDate.getFullYear() - 10 + i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <div
      style={{
        border: '1px solid #d0d0d0',
        borderRadius: '4px',
        padding: '12px',
        backgroundColor: '#f9f9f9',
      }}
    >
      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#003366' }}>
        月次自動処理（Step3）
      </div>
      <div style={{ fontSize: '10px', color: '#666', marginBottom: '12px' }}>
        指定した年月の減価償却費を計算し、仕訳を自動生成します。
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
          年:
          <select
            value={targetYear}
            onChange={(e) => setTargetYear(Number(e.target.value))}
            disabled={processing}
            style={{
              padding: '4px',
              fontSize: '11px',
              border: '1px inset #d0d0d0',
              backgroundColor: processing ? '#f5f5f5' : 'white',
            }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}年
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
          月:
          <select
            value={targetMonth}
            onChange={(e) => setTargetMonth(Number(e.target.value))}
            disabled={processing}
            style={{
              padding: '4px',
              fontSize: '11px',
              border: '1px inset #d0d0d0',
              backgroundColor: processing ? '#f5f5f5' : 'white',
            }}
          >
            {months.map((month) => (
              <option key={month} value={month}>
                {month}月
              </option>
            ))}
          </select>
        </label>

        <button
          onClick={handleExecute}
          disabled={processing}
          style={{
            padding: '6px 12px',
            fontSize: '11px',
            backgroundColor: processing ? '#ccc' : '#003366',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: processing ? 'not-allowed' : 'pointer',
          }}
        >
          {processing ? '処理中...' : '実行'}
        </button>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#ffebee',
            border: '1px solid #c62828',
            color: '#c62828',
            padding: '8px',
            fontSize: '11px',
            marginBottom: '8px',
            borderRadius: '4px',
          }}
        >
          ❌ {error}
        </div>
      )}

      {result && (
        <div
          style={{
            backgroundColor: '#e8f5e9',
            border: '1px solid #2e7d32',
            padding: '12px',
            fontSize: '11px',
            borderRadius: '4px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2e7d32' }}>
            ✅ 月次処理が完了しました
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '8px' }}>
            <div>処理対象固定資産数: {result.processed_count}件</div>
            <div>処理成功: {result.success_count}件</div>
            <div>処理エラー: {result.error_count}件</div>
            <div>総減価償却額: {result.total_depreciation_amount.toLocaleString()}円</div>
            <div style={{ gridColumn: '1 / -1' }}>
              生成された仕訳: {result.journal_entries_created}件
            </div>
          </div>
          {result.errors.length > 0 && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #2e7d32' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>エラー詳細:</div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {result.errors.map((err, idx) => (
                  <li key={idx} style={{ fontSize: '10px', color: '#c62828' }}>
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MonthlyProcessSection;












